import React, { ComponentProps } from 'react';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  DISPLAY_TYPE,
  createButtonObject,
} from '../../components/organisms/BlockInteractions';
import { Namespace } from '../../translation/namespace';
import { Icons } from '../../components/atoms/SVG';
import { DefaultView, EditView, Views } from '../../types/BlockWithViews';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { useContentWrapperContext } from '../../components/blocks/ContentWrapper/ContentWrapperContext';
import { BlockType } from '../../types/api/ElementType';
import { PersonCollectionDefaultView } from './views/PersonCollectionDefaultView';
import { PersonCollectionEditView } from './views/PersonCollectionEditView';
import { useContentWrapperDndContext } from '../../components/blocks/ContentWrapper/ContentWrapperDndProvider';
import { headlineHierarchy } from '../../components/organisms/BlockInteractions/BlockInteractions.utils';

type PersonCollectionBlockProps = Views<DefaultView<PersonCollectionDefaultView> & EditView<PersonCollectionEditView>>;

/**
 * Renders a search result or a form to create a search result.
 */
function PersonCollectionInteractions({ views, activeView }: PersonCollectionBlockProps) {
  const { t } = useSonTranslation('PersonCollectionBlock');
  const { uuid, elementType } = useBlockContext();
  const { refresh, refreshCount } = useRefreshBlock();
  const contentWrapperContext = useContentWrapperContext();
  const contentWrapperDndContext = useContentWrapperDndContext();
  const blockInteractionButtons = () => {
    const EditButton = createButtonObject(
      BlockInteractionsViews.EDIT,
      Icons.EDIT_PENCIL,
      `${Namespace.PersonCollectionBlock}:actions.edit`,
      DISPLAY_TYPE.COLLAPSIBLE,
      <PersonCollectionEditView {...views.edit} />
    );

    const DeleteButton = createButtonObject(
      BlockInteractionsViews.DELETE,
      Icons.ERASER,
      `${Namespace.PersonCollectionBlock}:buttons.delete`,
      DISPLAY_TYPE.MODAL
    );

    return { ...EditButton, ...DeleteButton };
  };

  return (
    <BlockInteractions
      contentWrapperContext={contentWrapperContext}
      contentWrapperDndContext={contentWrapperDndContext}
      headline={{
        text: t('headline'),
        hierarchy: headlineHierarchy(2, contentWrapperContext?.parentIndex, contentWrapperContext?.grandParentIndex),
        visible: true,
      }}
      alwaysShowInfo={false}
      views={views}
      activeView={activeView}
      buttons={blockInteractionButtons()}
      refreshBlock={refresh}
      refreshCount={refreshCount}
      uuid={uuid}
      elementType={elementType}
    >
      <PersonCollectionDefaultView {...views.default} />
    </BlockInteractions>
  );
}

export const PersonCollectionBlock = asFetchableBlock(PersonCollectionInteractions, BlockType.personcollection);
export type PersonCollectionBlock = ComponentProps<typeof PersonCollectionBlock>;
